<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="emoji"
        image="⏱️"
        :image-class="`transition-all duration-1000 ${isActive ? 'scale-110' : 'scale-100'}`"
        title="Pomodoro"
        subtitle="Toolbox"
        back="/tools"
        disable-edit-mode="true"
      >
      </round-header>
      <div class="flex justify-center">
        <div class="w-full max-w-[1024px] p-4 px-8 md:px-20">
          <div v-if="!selectedMode" class="space-y-4 text-center">
            <h2 class="font-fredoka font-outline-1 text-2xl text-white">Select a Pomodoro Mode</h2>
            <div class="grid grid-cols-3 gap-4">
              <button
                class="border-border-positive text-border-positive flex cursor-pointer flex-col items-center rounded-xl border-2 bg-[#ffffff33] p-4"
                @click="() => handleModeSelect('classic')"
              >
                <Timer class="h-6 w-6" />
                <span>Classic</span>
                <span class="text-xs">(25/5)</span>
              </button>
              <button
                class="border-border-positive text-border-positive flex cursor-pointer flex-col items-center rounded-xl border-2 bg-[#ffffff33] p-4"
                @click="() => handleModeSelect('short')"
              >
                <Timer class="h-6 w-6" />
                <span>Short</span>
                <span class="text-xs">(15/3)</span>
              </button>
              <button
                class="border-border-positive text-border-positive flex cursor-pointer flex-col items-center rounded-xl border-2 bg-[#ffffff33] p-4"
                @click="() => handleModeSelect('long')"
              >
                <Timer class="h-6 w-6" />
                <span>Long</span>
                <span class="text-xs">(50/10)</span>
              </button>
            </div>
          </div>

          <div v-else class="space-y-6">
            <div class="flex items-center gap-4">
              <Timer class="h-6 w-6 text-gray-600" />
              <div>
                <h3 class="font-fredoka font-outline-1 text-lg text-white">
                  {{ isBreak ? 'Break Time' : 'Focus Time' }}
                </h3>
                <p class="font-outline-1 text-sm text-white">
                  {{ pomodoroModes[selectedMode].name }} mode:
                  {{ pomodoroModes[selectedMode].work }}min work /
                  {{ pomodoroModes[selectedMode].break }}min break
                </p>
                <p class="font-outline-1 text-sm text-white">
                  Completed: {{ completedPomodoros }} / {{ totalPomodoros }}
                </p>
              </div>
            </div>

            <div class="rounded-xl bg-[#ffffff66] p-4 text-white">
              <h4 class="font-fredoka font-outline-1">
                {{ isBreak ? 'Break Time Tips' : 'Focus Time Tips' }}
              </h4>
              <ul class="font-outline-1 list-disc space-y-1 pl-4">
                <li v-for="(tip, index) in isBreak ? breakTips : focusTips" :key="index">
                  {{ tip }}
                </li>
              </ul>
            </div>

            <div class="space-y-2 text-center">
              <div
                class="relative h-10 overflow-hidden rounded-xl"
                :class="isBreak ? 'bg-[#d4edda]' : 'bg-[#ffffff99]'"
              >
                <div
                  :style="{ width: progress + '%' }"
                  :class="isBreak ? 'bg-green-500' : 'bg-bg-positive'"
                  class="absolute top-0 left-0 h-10 w-full rounded-xl transition-all duration-1000 ease-linear"
                ></div>
                <div
                  class="font-fredoka font-outline-1 relative z-10 pt-1 text-3xl text-white transition-all duration-1000"
                  :class="timerStarted ? 'opacity-20' : ''"
                >
                  {{ formatTime(timeLeft) }}
                </div>
              </div>
              <div class="mt-4 flex justify-center gap-4">
                <button
                  v-if="!timerStarted"
                  class="bg-fill-positive hover:bg-bg-positive cursor-pointer rounded-xl px-4 py-2 text-white transition-all duration-200"
                  @click="startTimer"
                >
                  <PlayCircle class="mr-1 inline h-5 w-5" />
                  Start
                </button>
                <template v-else>
                  <button
                    class="bg-bg-positive hover:bg-fill-positive flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border text-white transition-all duration-200"
                    @click="pauseTimer"
                  >
                    <PauseCircle v-if="isActive" class="h-6 w-6" />
                    <PlayCircle v-else class="h-6 w-6" />
                  </button>
                  <button
                    class="bg-bg-positive hover:bg-fill-positive flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border text-white transition-all duration-200"
                    @click="resetTimer"
                  >
                    <RotateCcw class="h-5 w-5" />
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div v-if="selectedMode" class="mt-6 flex justify-between">
            <button
              class="font-outline-1 text-sm text-white hover:underline"
              @click="
                () => {
                  selectedMode = null;
                  isActive = false;
                  resetTimer();
                }
              "
            >
              Back
            </button>
            <button
              v-if="!isBreak && timerStarted"
              class="font-outline-1 rounded border border-white px-3 py-1 text-sm text-white"
              @click="skipToBreak"
            >
              Skip to break
            </button>
            <button
              v-if="isBreak && timerStarted"
              class="font-outline-1 rounded border border-white px-3 py-1 text-sm text-white"
              @click="skipToWork"
            >
              Skip to work
            </button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue';
import { PlayCircle, PauseCircle, RotateCcw, Timer } from 'lucide-vue-next';
import { IonContent, IonPage, onIonViewWillLeave } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import * as Sentry from '@sentry/vue';

// Pomodoro modes with work/break durations in minutes
const pomodoroModes: any = {
  classic: { name: 'Classic', work: 25, break: 5 },
  short: { name: 'Short', work: 15, break: 3 },
  long: { name: 'Long', work: 50, break: 10 },
};

const selectedMode = ref<string | null>(null);
const timeLeft = ref<number>(0);
const isActive = ref<boolean>(false);
const progress = ref<number>(0);
const timerStarted = ref<boolean>(false);
const isBreak = ref<boolean>(false);
const completedPomodoros = ref<number>(0);
const totalPomodoros = ref<number>(4); // Default to 4 pomodoros

let interval: any = null;
const totalTime = ref<number>(0);
const bell: HTMLAudioElement = new Audio('/sounds/bell.mp3');
bell.load(); // Preload to avoid delay

// Tips for focus and break times
const focusTips = [
  'Focus on one task only',
  'Remove distractions (silence phone, close unnecessary tabs)',
  'Set a clear goal for this pomodoro session',
  'Use the "two-minute rule" - if a task takes less than two minutes, do it immediately',
  'Stay hydrated during your work session',
];

const breakTips = [
  'Stand up and stretch',
  'Look away from the screen and focus on something at least 20 feet away',
  'Take a few deep breaths',
  'Drink some water',
  'Avoid checking emails or social media during short breaks',
];

onUnmounted(() => {
  clearInterval(interval);
  bell.pause();
});

watch([isActive, timeLeft], () => {
  if (isActive.value && timeLeft.value > 0) {
    interval = setTimeout(() => {
      timeLeft.value--;
      progress.value = ((totalTime.value - timeLeft.value) / totalTime.value) * 100;
    }, 1000);
  } else if (isActive.value && timeLeft.value === 0) {
    bell.play();
    isActive.value = false;

    // Switch between work and break
    if (isBreak.value) {
      // Break is over, start a new work session
      isBreak.value = false;
      setupWorkTimer();
    } else {
      // Work is over, start a break
      isBreak.value = true;
      completedPomodoros.value++;
      setupBreakTimer();
    }
  } else {
    clearInterval(interval);
  }
});

const keepScreenOn = async () => {
  try {
    await ScreenOrientation.addListener('screenOrientationChange', () => {});
    await ScreenOrientation.lock({ orientation: 'portrait' });
  } catch (error) {
    Sentry.captureException(error);
  }
};

const releaseScreenLock = async () => {
  try {
    await ScreenOrientation.removeAllListeners();
    await ScreenOrientation.unlock();
  } catch (error) {
    Sentry.captureException(error);
  }
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const handleModeSelect = (mode: string) => {
  selectedMode.value = mode;
  completedPomodoros.value = 0;
  isBreak.value = false;
  setupWorkTimer();
};

const setupWorkTimer = () => {
  if (!selectedMode.value) return;

  const workMinutes = pomodoroModes[selectedMode.value as keyof typeof pomodoroModes].work;
  timeLeft.value = workMinutes * 60;
  totalTime.value = workMinutes * 60;
  progress.value = 0;
  timerStarted.value = false;
};

const setupBreakTimer = () => {
  if (!selectedMode.value) return;

  const breakMinutes = pomodoroModes[selectedMode.value as keyof typeof pomodoroModes].break;
  timeLeft.value = breakMinutes * 60;
  totalTime.value = breakMinutes * 60;
  progress.value = 0;

  // Auto-start the break timer
  if (timerStarted.value) {
    startTimer();
  }
};

const startTimer = async () => {
  if (bell) {
    bell.play().catch(() => {
      console.log('blocked!');
    });
    bell.pause();
    bell.currentTime = 0;
  }

  isActive.value = true;
  timerStarted.value = true;
  await keepScreenOn();
};

const pauseTimer = async () => {
  isActive.value = !isActive.value;

  if (isActive.value) {
    await keepScreenOn();
  } else {
    await releaseScreenLock();
  }
};

const resetTimer = async () => {
  isActive.value = false;
  timerStarted.value = false;
  isBreak.value = false;
  completedPomodoros.value = 0;

  if (selectedMode.value) {
    setupWorkTimer();
  }

  await releaseScreenLock();
};

const skipToBreak = () => {
  if (!isBreak.value) {
    isActive.value = false;
    isBreak.value = true;
    completedPomodoros.value++;
    setupBreakTimer();
  }
};

const skipToWork = () => {
  if (isBreak.value) {
    isActive.value = false;
    isBreak.value = false;
    setupWorkTimer();
  }
};

onIonViewWillLeave(() => {
  resetTimer();
});
</script>

<style scoped>
/* Optional scoped styles if needed */
</style>
